<template>
    <div
        v-if="text"
        class="prose max-w-full"
        :class="[
            columnsClass,
            {
                'prose-h1:!leading-none': headingSize,
                'prose-h1:!text-7xl': headingSize === 'lg',
                'prose-h1:!text-8xl': headingSize === 'xl',
                'prose-h1:!text-9xl': headingSize === '2xl',
                'prose-p:!leading-tight': bodyTextSize,
                'prose-p:!text-xl': bodyTextSize === 'lg',
                'prose-p:!text-2xl': bodyTextSize === 'xl',
                'prose-p:!text-3xl': bodyTextSize === '2xl',
            },
        ]"
        v-html="text ?? ''"
    />
</template>

<script setup>
    const props = defineProps({
        text: String,
        cols: Number,
        headingSize: String,
        bodyTextSize: String,
    })

    const columnsClass = computed(() => {
        switch (props.cols) {
            case 2:
                return 'md:columns-2'
            case 3:
                return 'md:columns-3'
            case 4:
                return 'md:columns-4'
            default:
                return ''
        }
    })
</script>

<style>
    @reference 'tailwindcss';
    @reference '@digistorm/spark/dist/variables.css';
    @reference '@digistorm/spark/brands/websites.css';

    .prose h1,
    .prose h2,
    .prose h3,
    .prose h4,
    .prose h5,
    .prose h6 {
        @apply font-brand-heading text-heading font-heading;
    }
    .prose h1 {
        @apply leading-6xl mt-0 mb-6 text-6xl;
    }
    .prose h2 {
        @apply leading-5xl mt-0 mb-5 text-5xl;
    }
    .prose h2.text-subheading {
        color: var(--text-subheading);
        @apply font-ux leading-base text-base font-(--font-weight-subheading)! uppercase;
    }
    .prose h2.text-subheading strong,
    .prose h2.text-subheading b {
        color: var(--text-subheading);
        @apply font-(--font-weight-subheading-strong)!;
    }
    .prose h3 {
        @apply leading-4xl mb-4 text-4xl;
    }
    .prose h4 {
        @apply leading-3xl mb-4 text-3xl;
    }
    .prose h4.text-lead-text {
        @apply text-brand-strong leading-xl font-brand-lead text-lg;
    }
    .prose h4.text-lead-text strong,
    .prose h4.text-lead-text b {
        @apply text-(--text-lead-text);
    }

    .prose h5 {
        @apply leading-2xl mb-4 text-2xl;
    }
    .prose h6 {
        @apply leading-xl mb-4 text-xl;
    }
    .prose h1 strong,
    .prose h1 b,
    .prose h2 strong,
    .prose h2 b,
    .prose h3 strong,
    .prose h3 b,
    .prose h4 strong,
    .prose h4 b,
    .prose h5 strong,
    .prose h5 b,
    .prose h6 strong,
    .prose h6 b {
        @apply text-heading font-(--font-weight-heading-strong)!;
    }
    .prose p,
    .prose li {
        @apply leading-base font-ux text-base font-(--font-weight-regular)! text-(--text-body);
    }
    .prose p::marker,
    .prose li::marker {
        @apply text-(--text-body);
    }
    .prose p strong,
    .prose p b,
    .prose li strong,
    .prose li b {
        @apply font-(--font-weight-ux-strong)! text-(--text-body);
    }

    .prose img {
        @apply rounded-image-default aspect-auto! overflow-hidden;
    }
    .prose figcaption {
        @apply text-neutral-subtle;
    }
    .prose a {
        @apply button-transparent-on-surface-brand-default hover:button-transparent-on-surface-brand-hover cursor-pointer underline underline-offset-2 transition-colors duration-75 focus-visible:z-20 focus-visible:outline-2 focus-visible:outline-offset-2;
    }
    .prose.prose-lg h1 {
        @apply text-7xl leading-none;
    }
    .prose.prose-xl h1 {
        @apply text-8xl leading-none;
    }
    .prose.prose-2xl h1 {
        @apply text-9xl leading-none;
    }

    /* ckeditor classes */
    .image {
        @apply w-fit;
    }

    .image-style-align-center {
        @apply mx-auto block;
    }

    .image-style-block-align-left {
        @apply block;
    }

    .image-style-block-align-right {
        @apply ml-auto block;
    }

    .image-style-align-left {
        @variant md {
            @apply float-left my-2 mr-6;
        }
    }

    .image-style-align-right {
        @variant md {
            @apply float-right my-2 ml-6;
        }
    }
</style>
